import React, { useState, useEffect } from "react";
import {
  ContentLayout,
  Container,
  Header,
  SpaceBetween,
  Button,
  Table,
  Box,
  Modal,
  FormField,
  Input,
  Textarea,
  Toggle,
  Flashbar,
  TextFilter,
  Pagination,
  Badge,
  HelpPanel,
  Link,
} from "@cloudscape-design/components";
import { api } from "../api";
import { useTranslation } from "react-i18next";

function SourceFeedsPage({ setToolsOpen, setToolsContent }) {
  const { t } = useTranslation();
  const [feeds, setFeeds] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedItems, setSelectedItems] = useState([]);
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [flashbarItems, setFlashbarItems] = useState([]);
  const [filteringText, setFilteringText] = useState("");
  const [currentPageIndex, setCurrentPageIndex] = useState(1);
  const pageSize = 20;

  // Form state
  const [formName, setFormName] = useState("");
  const [formUrl, setFormUrl] = useState("");
  const [formDescription, setFormDescription] = useState("");
  const [formEnabled, setFormEnabled] = useState(true);
  const [editingFeed, setEditingFeed] = useState(null);
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    loadFeeds();
  }, []);

  useEffect(() => {
    setToolsContent(
      <HelpPanel header={<h2>{t("source-feeds.help.title")}</h2>}>
        <SpaceBetween size="m">
          <Box>
            <p>{t("source-feeds.help.intro")}</p>
          </Box>
          <Box>
            <h3>{t("source-feeds.help.adding-title")}</h3>
            <p>{t("source-feeds.help.adding-desc")}</p>
          </Box>
          <Box>
            <h3>{t("source-feeds.help.processing-title")}</h3>
            <p>{t("source-feeds.help.processing-desc")}</p>
          </Box>
        </SpaceBetween>
      </HelpPanel>,
    );
    setToolsOpen(true);
  }, [t]);

  const loadFeeds = async () => {
    setLoading(true);
    try {
      const data = await api.listSourceFeeds();
      setFeeds(data.source_feeds || []);
    } catch (e) {
      showError(e.message);
    } finally {
      setLoading(false);
    }
  };

  const showError = (msg) => {
    setFlashbarItems([{ type: "error", content: msg, dismissible: true, onDismiss: () => setFlashbarItems([]) }]);
  };

  const showSuccess = (msg) => {
    setFlashbarItems([{ type: "success", content: msg, dismissible: true, onDismiss: () => setFlashbarItems([]) }]);
  };

  const resetForm = () => {
    setFormName("");
    setFormUrl("");
    setFormDescription("");
    setFormEnabled(true);
    setEditingFeed(null);
  };

  const openCreate = () => {
    resetForm();
    setShowCreateModal(true);
  };

  const openEdit = () => {
    const feed = selectedItems[0];
    setFormName(feed.name || "");
    setFormUrl(feed.url || "");
    setFormDescription(feed.description || "");
    setFormEnabled(feed.enabled !== false);
    setEditingFeed(feed);
    setShowEditModal(true);
  };

  const handleCreate = async () => {
    if (!formName.trim() || !formUrl.trim()) {
      showError(t("source-feeds.error.name-url-required"));
      return;
    }
    setSubmitting(true);
    try {
      await api.createSourceFeed({ name: formName.trim(), url: formUrl.trim(), description: formDescription.trim(), enabled: formEnabled });
      showSuccess(t("source-feeds.notify.created"));
      setShowCreateModal(false);
      resetForm();
      loadFeeds();
    } catch (e) {
      showError(e.message);
    } finally {
      setSubmitting(false);
    }
  };

  const handleEdit = async () => {
    if (!formName.trim() || !formUrl.trim()) {
      showError(t("source-feeds.error.name-url-required"));
      return;
    }
    setSubmitting(true);
    try {
      await api.updateSourceFeed(editingFeed.feed_id, {
        name: formName.trim(),
        url: formUrl.trim(),
        description: formDescription.trim(),
        enabled: formEnabled,
      });
      showSuccess(t("source-feeds.notify.updated"));
      setShowEditModal(false);
      resetForm();
      setSelectedItems([]);
      loadFeeds();
    } catch (e) {
      showError(e.message);
    } finally {
      setSubmitting(false);
    }
  };

  const handleDelete = async () => {
    setSubmitting(true);
    try {
      for (const feed of selectedItems) {
        await api.deleteSourceFeed(feed.feed_id);
      }
      showSuccess(t("source-feeds.notify.deleted"));
      setShowDeleteModal(false);
      setSelectedItems([]);
      loadFeeds();
    } catch (e) {
      showError(e.message);
    } finally {
      setSubmitting(false);
    }
  };

  const filtered = feeds.filter(
    (f) => !filteringText || f.name?.toLowerCase().includes(filteringText.toLowerCase()) || f.url?.toLowerCase().includes(filteringText.toLowerCase()),
  );

  const paginated = filtered.slice((currentPageIndex - 1) * pageSize, currentPageIndex * pageSize);

  const formContent = (
    <SpaceBetween size="m">
      <FormField label={t("source-feeds.field.name")} description={t("source-feeds.field.name-desc")}>
        <Input value={formName} onChange={({ detail }) => setFormName(detail.value)} placeholder={t("source-feeds.field.name-placeholder")} />
      </FormField>
      <FormField label={t("source-feeds.field.url")} description={t("source-feeds.field.url-desc")}>
        <Input value={formUrl} onChange={({ detail }) => setFormUrl(detail.value)} placeholder="https://example.com/feed.xml" />
      </FormField>
      <FormField label={t("source-feeds.field.description")}>
        <Textarea
          value={formDescription}
          onChange={({ detail }) => setFormDescription(detail.value)}
          placeholder={t("source-feeds.field.description-placeholder")}
          rows={2}
        />
      </FormField>
      <FormField label={t("source-feeds.field.enabled")}>
        <Toggle checked={formEnabled} onChange={({ detail }) => setFormEnabled(detail.checked)}>
          {formEnabled ? t("source-feeds.status.enabled") : t("source-feeds.status.disabled")}
        </Toggle>
      </FormField>
    </SpaceBetween>
  );

  return (
    <ContentLayout header={<Header variant="h1">{t("source-feeds.page.title")}</Header>}>
      <SpaceBetween size="m">
        <Flashbar items={flashbarItems} />
        <Container>
          <Table
            loading={loading}
            loadingText={t("source-feeds.list.loading")}
            items={paginated}
            selectedItems={selectedItems}
            onSelectionChange={({ detail }) => setSelectedItems(detail.selectedItems)}
            selectionType="multi"
            trackBy="feed_id"
            empty={<Box textAlign="center">{t("source-feeds.list.empty")}</Box>}
            filter={
              <TextFilter
                filteringText={filteringText}
                filteringPlaceholder={t("source-feeds.list.search")}
                onChange={({ detail }) => {
                  setFilteringText(detail.filteringText);
                  setCurrentPageIndex(1);
                }}
              />
            }
            header={
              <Header
                variant="h2"
                counter={`(${filtered.length})`}
                actions={
                  <SpaceBetween direction="horizontal" size="xs">
                    <Button onClick={loadFeeds} iconName="refresh">
                      {t("source-feeds.action.refresh")}
                    </Button>
                    <Button disabled={selectedItems.length !== 1} onClick={openEdit}>
                      {t("source-feeds.action.edit")}
                    </Button>
                    <Button disabled={selectedItems.length === 0} onClick={() => setShowDeleteModal(true)}>
                      {t("source-feeds.action.delete")}
                    </Button>
                    <Button variant="primary" onClick={openCreate}>
                      {t("source-feeds.action.add")}
                    </Button>
                  </SpaceBetween>
                }
              >
                {t("source-feeds.page.title")}
              </Header>
            }
            pagination={
              <Pagination
                currentPageIndex={currentPageIndex}
                pagesCount={Math.max(1, Math.ceil(filtered.length / pageSize))}
                onChange={({ detail }) => setCurrentPageIndex(detail.currentPageIndex)}
              />
            }
            columnDefinitions={[
              {
                id: "name",
                header: t("source-feeds.column.name"),
                cell: (item) => item.name,
                sortingField: "name",
              },
              {
                id: "url",
                header: t("source-feeds.column.url"),
                cell: (item) => {
                  const url = item.url || "";
                  if (!url.startsWith("http://") && !url.startsWith("https://")) {
                    return url.length > 60 ? url.slice(0, 60) + "…" : url;
                  }
                  return (
                    // nosemgrep: react-href-var
                    // URL is validated to start with http(s):// above
                    <Link href={url} external fontSize="body-s">
                      {url.length > 60 ? url.slice(0, 60) + "…" : url}
                    </Link>
                  );
                },
              },
              {
                id: "description",
                header: t("source-feeds.column.description"),
                cell: (item) => item.description || "—",
              },
              {
                id: "enabled",
                header: t("source-feeds.column.status"),
                cell: (item) => (
                  <Badge color={item.enabled !== false ? "green" : "grey"}>
                    {item.enabled !== false ? t("source-feeds.status.enabled") : t("source-feeds.status.disabled")}
                  </Badge>
                ),
              },
              {
                id: "created_at",
                header: t("source-feeds.column.added"),
                cell: (item) => (item.created_at ? new Date(item.created_at).toLocaleDateString() : "—"),
              },
            ]}
          />
        </Container>

        {/* Create Modal */}
        <Modal
          visible={showCreateModal}
          onDismiss={() => {
            setShowCreateModal(false);
            resetForm();
          }}
          header={t("source-feeds.modal.create-title")}
          footer={
            <Box float="right">
              <SpaceBetween direction="horizontal" size="xs">
                <Button
                  onClick={() => {
                    setShowCreateModal(false);
                    resetForm();
                  }}
                >
                  {t("source-feeds.action.cancel")}
                </Button>
                <Button variant="primary" loading={submitting} onClick={handleCreate}>
                  {t("source-feeds.action.create")}
                </Button>
              </SpaceBetween>
            </Box>
          }
        >
          {formContent}
        </Modal>

        {/* Edit Modal */}
        <Modal
          visible={showEditModal}
          onDismiss={() => {
            setShowEditModal(false);
            resetForm();
          }}
          header={t("source-feeds.modal.edit-title")}
          footer={
            <Box float="right">
              <SpaceBetween direction="horizontal" size="xs">
                <Button
                  onClick={() => {
                    setShowEditModal(false);
                    resetForm();
                  }}
                >
                  {t("source-feeds.action.cancel")}
                </Button>
                <Button variant="primary" loading={submitting} onClick={handleEdit}>
                  {t("source-feeds.action.save")}
                </Button>
              </SpaceBetween>
            </Box>
          }
        >
          {formContent}
        </Modal>

        {/* Delete Modal */}
        <Modal
          visible={showDeleteModal}
          onDismiss={() => setShowDeleteModal(false)}
          header={t("source-feeds.modal.delete-title")}
          footer={
            <Box float="right">
              <SpaceBetween direction="horizontal" size="xs">
                <Button onClick={() => setShowDeleteModal(false)}>{t("source-feeds.action.cancel")}</Button>
                <Button variant="primary" loading={submitting} onClick={handleDelete}>
                  {t("source-feeds.action.delete")}
                </Button>
              </SpaceBetween>
            </Box>
          }
        >
          <Box>{t("source-feeds.modal.delete-confirm", { count: selectedItems.length })}</Box>
        </Modal>
      </SpaceBetween>
    </ContentLayout>
  );
}

export default SourceFeedsPage;
